'use client'

/**
 * Global Error Boundary
 *
 * Catches errors in the ROOT LAYOUT itself — the provider chain,
 * TerminologyProviderWrapper, ImpersonationWrapper, SessionProvider,
 * etc. This is the last line of defense against blank white screens.
 *
 * Must provide its own <html>/<body> since the root layout may have
 * failed to render.
 *
 * MUST have zero app-level imports. Issue #5544: on 2026-04-21, prod
 * `/admin` and `/admin/feedback` crashed with "Cannot find module" for
 * server SSR chunks that were referenced but missing from the deployed
 * build. The root error boundary also failed to render because it
 * shared chunks with the broken page. If this file imports anything
 * from `@/components`, `@/lib`, `@/app/actions`, or any icon library,
 * those imports re-create the same chunk dependency that this boundary
 * exists to survive. Log reporting goes through a plain `fetch()` to
 * `/api/log-client-error` (a route handler with its own chunk graph)
 * rather than the `logClientError` server action.
 *
 * Chunk-load errors (#15676): during a rolling deploy a page built by one
 * release can request a chunk from a pod running another and get a 404.
 * A `ChunkLoadError` caught here never reaches the window listeners in
 * `src/lib/chunk-load-recovery.ts`, so this boundary reloads once per
 * deployment itself and, when that guard refuses, offers a full reload
 * rather than `reset()` (which re-renders the same stale client). The guard
 * is an inline copy of that module's `reloadOncePerDeployment` because of
 * the import rule above — same storage key, same semantics, pinned by
 * `tests/unit/app/global-error-chunk-recovery-15676.test.tsx`.
 *
 * Network failures (#15979): a browser-side `TypeError` whose message is a
 * fetch failure (Safari "Load failed", Chromium "Failed to fetch", Firefox
 * "NetworkError when attempting to fetch resource.") means the browser got
 * no response — blocked before the app, offline, or a connection dropped
 * mid-response — not that a render failed. It gets connection copy instead
 * of the database copy, "Try again" calls `retry()` (which re-fetches;
 * `reset()` would re-render without re-fetching), and it is reported as
 * `global-error-boundary-network`, pinned by
 * `tests/unit/app/global-error-network-15979.test.tsx`.
 *
 * Common causes on preview deployments:
 * - DATABASE_URL not configured or unreachable
 * - Auth provider misconfiguration (GOOGLE_CLIENT_ID/SECRET)
 * - Missing environment variables
 */

import { useEffect } from 'react'

/**
 * Inline copy of `CHUNK_LOAD_RELOAD_SESSION_KEY` from
 * `src/lib/chunk-load-recovery.ts`. Change both together.
 */
const CHUNK_RELOAD_STORAGE_KEY = 'cadence-chunk-load-reload'

/** Stored when the deployment id is unknown: the once-per-session rule. */
const UNKNOWN_DEPLOYMENT_MARKER = '1'
/** Separates remembered deployment ids; commit SHAs never contain it. */
const DEPLOYMENT_ID_SEPARATOR = ' '
/** Bound on remembered deployment ids, as in the lib guard. */
const MAX_REMEMBERED_DEPLOYMENTS = 20

const NEXT_STATIC_PATH = '/_next/static/'

const CHUNK_LOAD_MESSAGE_PATTERNS: readonly RegExp[] = [
  /ChunkLoadError/,
  /Loading (CSS )?chunk [^\s]+ failed/i,
  /Failed to load chunk /i,
  /Importing a module script failed/i,
]

const DYNAMIC_IMPORT_MESSAGE_PATTERN =
  /(Failed to fetch|error loading) dynamically imported module/i

/** The browsers' spellings of a fetch that never got a response. */
const NETWORK_FAILURE_MESSAGES: ReadonlySet<string> = new Set([
  // Safari
  'Load failed',
  // Chromium
  'Failed to fetch',
  // Firefox
  'NetworkError when attempting to fetch resource.',
])

/**
 * A fetch the browser could not complete: no response came back, so no
 * server render failed. That covers a blocked or offline request and a
 * connection dropped mid-response alike, so it is counted on its own rather
 * than as a client-only fault. Exact messages only, so a `TypeError` from
 * app code is still reported as a server-side failure.
 */
function isNetworkFailure(error: Error): boolean {
  return (
    error.name === 'TypeError' &&
    typeof error.message === 'string' &&
    NETWORK_FAILURE_MESSAGES.has(error.message)
  )
}

function pageOrigin(): string | undefined {
  return typeof window === 'undefined' ? undefined : window.location.origin
}

/** Minimal inline `isChunkLoadError`: a failed load of this app's chunk. */
function isChunkLoadFailure(error: Error): boolean {
  if (error.name === 'ChunkLoadError') return true
  const message = typeof error.message === 'string' ? error.message : ''
  if (CHUNK_LOAD_MESSAGE_PATTERNS.some(pattern => pattern.test(message))) {
    return true
  }
  const origin = pageOrigin()
  return (
    DYNAMIC_IMPORT_MESSAGE_PATTERN.test(message) &&
    message.includes(`${origin ?? ''}${NEXT_STATIC_PATH}`)
  )
}

/**
 * The deployment the page is running: Next's `NEXT_DEPLOYMENT_ID` global,
 * else the `dpl` query on the page's own `/_next/static` scripts.
 */
function readDeploymentId(): string | null {
  try {
    const fromNext = (globalThis as { NEXT_DEPLOYMENT_ID?: unknown })
      .NEXT_DEPLOYMENT_ID
    if (typeof fromNext === 'string' && fromNext !== '') return fromNext
    const origin = window.location.origin
    for (const script of Array.from(document.scripts)) {
      if (!script.src) continue
      let url: URL
      try {
        url = new URL(script.src, origin)
      } catch (_unparseableUrl) {
        continue
      }
      if (url.origin !== origin) continue
      if (!url.pathname.startsWith(NEXT_STATIC_PATH)) continue
      const id = url.searchParams.get('dpl')
      if (id) return id
    }
  } catch (_unreadable) {
    // Unknown deployment: fall back to once per session.
  }
  return null
}

function hardReload(): void {
  // biome-ignore lint/plugin/no-window-location-reload: this file may not import safeRefresh (#5544)
  window.location.reload()
}

/**
 * Inline `reloadOncePerDeployment`: reload unless this tab already reloaded
 * for the deployment it is running — any it has reloaded for, so a
 * mid-rollout A, B, A sequence cannot loop — or, with no readable id, at
 * all. Any storage failure, including a write that does not stick, means no
 * reload.
 */
function reloadOncePerDeployment(): boolean {
  const deploymentId = readDeploymentId()
  try {
    const storage = window.sessionStorage
    const previous = storage.getItem(CHUNK_RELOAD_STORAGE_KEY)
    const remembered = (previous ?? '')
      .split(DEPLOYMENT_ID_SEPARATOR)
      .filter(id => id !== '')
    if (deploymentId === null) {
      if (previous !== null) return false
    } else if (remembered.includes(deploymentId)) {
      return false
    }
    const marker =
      deploymentId === null
        ? UNKNOWN_DEPLOYMENT_MARKER
        : [...remembered, deploymentId]
            .slice(-MAX_REMEMBERED_DEPLOYMENTS)
            .join(DEPLOYMENT_ID_SEPARATOR)
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, marker)
    if (storage.getItem(CHUNK_RELOAD_STORAGE_KEY) !== marker) return false
  } catch (_storageUnavailable) {
    return false
  }
  hardReload()
  return true
}

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
  retry?: () => void
}

export default function GlobalError({ error, reset, retry }: GlobalErrorProps) {
  const isChunkError = isChunkLoadFailure(error)
  const isNetworkError = !isChunkError && isNetworkFailure(error)

  useEffect(() => {
    console.info('[Global error boundary]', error)

    // Issue #5520: forward to server for structured logs. Posted via
    // fetch — NOT via the logClientError server action — to keep this
    // file free of @/app/actions imports (see file header).
    //
    // `keepalive: true` has a ~64KB body cap in all major browsers.
    // The server side (/api/log-client-error) enforces the matching
    // limit, so an oversized React stack + componentStack will be
    // rejected symmetrically rather than silently truncated on one side.
    const payload = {
      source: isNetworkError
        ? 'global-error-boundary-network'
        : 'global-error-boundary',
      message: error.message,
      stack: error.stack ?? null,
      componentStack: null,
      digest: error.digest ?? null,
      url: typeof window !== 'undefined' ? window.location.href : '(server)',
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : '(server)',
    }
    fetch('/api/log-client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {})
  }, [error, isNetworkError])

  // Runs after the report above is queued (`keepalive` lets it outlive the
  // reload). When the guard refuses, the card offers the reload by hand.
  useEffect(() => {
    if (isChunkError) reloadOncePerDeployment()
  }, [isChunkError])

  // Inline styles only (see header), so SchoolAI design-system values are
  // written as literals: black-80 / black-56 text, blue-500 action,
  // --shadow-sm, z1-light page (src/tokens/index.css).
  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          backgroundColor: '#f2f2f2',
          color: 'hsl(0 0% 0% / 0.8)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '24px',
          }}
        >
          <div
            data-testid="global-error-boundary"
            style={{
              maxWidth: '480px',
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center',
              boxShadow: '0 4px 12px -2px hsl(0 100% 4% / 0.12)',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '24px',
              }}
            >
              !
            </div>

            <h1
              style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '8px',
              }}
            >
              {isChunkError
                ? 'A new version of SchoolAI Admin is available'
                : 'SchoolAI Admin failed to load'}
            </h1>

            <p
              data-testid={isNetworkError ? 'global-error-network' : undefined}
              style={{
                fontSize: '14px',
                color: 'hsl(0 0% 0% / 0.56)',
                marginBottom: '24px',
                lineHeight: 1.5,
              }}
            >
              {isChunkError
                ? 'SchoolAI Admin was updated while this page was open. ' +
                  'Reload the page to get the latest version.'
                : isNetworkError
                  ? "We couldn't reach SchoolAI Admin. " +
                    'Check your connection and try again.'
                  : 'The application encountered an error during ' +
                    'initialization. This usually means a database ' +
                    'connection or configuration issue.'}
            </p>

            <button
              type="button"
              onClick={
                isChunkError
                  ? hardReload
                  : isNetworkError
                    ? (retry ?? hardReload)
                    : reset
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 20px',
                minHeight: '44px',
                backgroundColor: 'hsl(215 90% 50%)',
                color: '#fff',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {isChunkError ? 'Reload page' : 'Try again'}
            </button>

            {error.digest && (
              <p
                style={{
                  marginTop: '16px',
                  fontSize: '12px',
                  color: '#94a3b8',
                }}
              >
                Error ID: {error.digest}
              </p>
            )}

            {error.message && (
              <details
                style={{
                  marginTop: '16px',
                  textAlign: 'left',
                }}
              >
                <summary
                  style={{
                    fontSize: '12px',
                    color: '#94a3b8',
                    cursor: 'pointer',
                  }}
                >
                  Technical details
                </summary>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(error.message)
                    }}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '8px',
                      padding: '2px 8px',
                      fontSize: '10px',
                      color: '#64748b',
                      backgroundColor: '#e2e8f0',
                      border: '1px solid #cbd5e1',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Copy
                  </button>
                  <pre
                    style={{
                      marginTop: '8px',
                      padding: '12px',
                      paddingRight: '60px',
                      backgroundColor: '#f1f5f9',
                      borderRadius: '8px',
                      fontSize: '11px',
                      color: '#dc2626',
                      overflow: 'auto',
                      maxHeight: '160px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {error.message}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      </body>
    </html>
  )
}
